"use client";

/**
 * Audio graphics drawn from real BB tracks (Gordon 2026-11-03: "all audio
 * graphics must be authentic — now they look like a bad copy of something
 * authentic"). The data is measured once with ffmpeg (film-art.ts): the
 * waveform scrolls past a fixed playhead at real speed, the loudness meter
 * plays back the track's measured momentary loudness, the numbers are the
 * measured ones. Black & white; the cover is the only colour.
 */

import { useEffect, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import type { FilmAudioTrack } from "./film-art";

const FG = "#f5f1ea";
const EXCERPT_S = 30;

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const db = (v: number | null | undefined, unit: string) => (v == null ? "—" : `${v < 0 ? "−" : v > 0 ? "+" : ""}${Math.abs(v).toFixed(1)} ${unit}`);

/** Seconds since mount, ticking. */
function useElapsed(stepMs = 250) {
  const [t, setT] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const id = window.setInterval(() => setT((performance.now() - start) / 1000), stepMs);
    return () => window.clearInterval(id);
  }, [stepMs]);
  return t;
}

/** The excerpt's waveform scrolling past a playhead at real speed; `seconds` visible. */
export function ScrollWave({ track, seconds = 8, head = 0.3, className, mirror = true }: {
  track: FilmAudioTrack; seconds?: number; head?: number; className?: string; mirror?: boolean;
}) {
  const bins = track.excerpt;
  const travel = EXCERPT_S - seconds;
  return (
    <div className={cn("relative overflow-hidden", className)}>
      <div
        className="absolute inset-y-0 left-0"
        style={{
          width: `${(EXCERPT_S / seconds) * 100}%`,
          ["--to" as string]: `-${(travel / EXCERPT_S) * 100}%`,
          animation: `sm-scroll ${travel}s linear infinite`,
        }}
      >
        <svg viewBox={`0 0 ${bins.length} 100`} preserveAspectRatio="none" className="h-full w-full" aria-hidden>
          {bins.map((v, i) => {
            const h = Math.max(1.2, v);
            return <rect key={i} x={i + 0.18} width={0.64} y={mirror ? 50 - h / 2 : 100 - h} height={h} fill={FG} />;
          })}
        </svg>
      </div>
      {/* What's still to come is dimmer, like BB's player. */}
      <div className="absolute inset-y-0 right-0 bg-[#171512]/55" style={{ left: `${head * 100}%` }} />
      <span className="absolute inset-y-0 w-[0.22cqw] bg-white" style={{ left: `${head * 100}%` }} />
    </div>
  );
}

/** Where the playhead is in the real track. */
function TimeCode({ track, head, seconds }: { track: FilmAudioTrack; head: number; seconds: number }) {
  const t = useElapsed();
  const at = track.excerptFrom + head * seconds + (t % (EXCERPT_S - seconds));
  return <span className="tabular-nums">{mmss(at)} / {mmss(track.duration)}</span>;
}

function Cover({ src, className }: { src?: string; className?: string }) {
  return (
    <div className={cn("relative shrink-0 overflow-hidden rounded-[0.5cqw] bg-white/10", className)}>
      {src && <Image src={src} alt="" fill sizes="12vw" className="object-cover" />}
    </div>
  );
}

/** A track as BB's player shows it: cover, title, artist, time, waveform. */
export function RealTrack({ track }: { track: FilmAudioTrack }) {
  return (
    <div className="sm-in w-[44cqw] space-y-[1.2cqw]">
      <div className="flex items-center gap-[1.2cqw]">
        <Cover src={track.cover} className="h-[8cqw] w-[8cqw]" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[2.2cqw] font-bold leading-tight">{track.title}</p>
          <p className="truncate text-[1.7cqw] opacity-75">{track.artist}</p>
          <p className="text-[1.5cqw] opacity-60"><TimeCode track={track} head={0.3} seconds={8} /></p>
        </div>
      </div>
      <ScrollWave track={track} className="h-[12cqw]" />
    </div>
  );
}

/**
 * An EBU R128 meter replaying the track's measured momentary loudness (M) and
 * the short-term average (S, 3 s), with the measured integrated loudness and
 * true peak, against the streaming target.
 */
export function RealLoudness({ track, targetLabel, target = -14 }: { track: FilmAudioTrack; targetLabel: string; target?: number }) {
  const t = useElapsed(400);
  const m = track.momentary;
  const i = m.length ? Math.floor(t / 0.4) % m.length : 0;
  const M = m[i] ?? -40;
  const window3s = m.slice(Math.max(0, i - 7), i + 1);
  const S = window3s.reduce((a, b) => a + b, 0) / (window3s.length || 1);
  const h = (v: number) => `${Math.max(0, Math.min(1, (v + 40) / 40)) * 100}%`;
  const scale = [0, -6, -14, -23, -40];
  return (
    <div className="sm-in flex items-stretch gap-[2.4cqw]">
      <div className="flex items-end gap-[1cqw]">
        {[{ k: "M", v: M }, { k: "S", v: S }].map(({ k, v }) => (
          <div key={k} className="flex flex-col items-center gap-[0.6cqw]">
            <div className="relative h-[24cqw] w-[3.4cqw] overflow-hidden rounded-[0.4cqw] bg-white/10">
              <span className="absolute inset-x-0 bottom-0 bg-white transition-[height] duration-300 ease-out" style={{ height: h(v) }} />
              {/* The target line */}
              <span className="absolute inset-x-0 h-[0.25cqw] bg-[#171512]" style={{ bottom: h(target) }} />
            </div>
            <span className="text-[1.4cqw] font-bold">{k}</span>
          </div>
        ))}
        <div className="relative h-[24cqw] text-[1.2cqw] tabular-nums opacity-60">
          {scale.map(v => <span key={v} className="absolute left-0 -translate-y-1/2" style={{ bottom: h(v) }}>{v}</span>)}
        </div>
      </div>
      <div className="flex min-w-[20cqw] flex-col justify-between py-[0.5cqw]">
        <div>
          <p className="truncate text-[1.6cqw] font-semibold opacity-75">{track.title} · {track.artist}</p>
          <p className="text-[4.6cqw] font-bold tabular-nums leading-none">{db(M, "LUFS")}</p>
          <p className="text-[1.5cqw] opacity-60">M · S {db(Math.round(S * 10) / 10, "LUFS")}</p>
        </div>
        <div className="space-y-[0.4cqw] text-[1.7cqw] tabular-nums">
          <p>I {db(track.lufs, "LUFS")}</p>
          <p>TP {db(track.truePeak, "dBTP")}</p>
          <p className="font-bold">→ {db(target, "LUFS")} · {targetLabel}</p>
        </div>
      </div>
    </div>
  );
}

/** Two real tracks with the same tempo, stacked like BB DJ's waveforms. */
export function RealDecks({ a, b }: { a: FilmAudioTrack; b: FilmAudioTrack }) {
  const synced = a.bpm != null && b.bpm != null && Math.abs(a.bpm - b.bpm) < 0.5;
  return (
    <div className="sm-in w-[48cqw] space-y-[1cqw]">
      {[a, b].map((tr, n) => (
        <div key={tr.id} className="space-y-[0.6cqw]">
          <div className="flex items-center gap-[1cqw] text-[1.6cqw]">
            <span className="grid h-[3cqw] w-[3cqw] shrink-0 place-items-center rounded-[0.4cqw] border border-white/60 font-bold">{n ? "B" : "A"}</span>
            <Cover src={tr.cover} className="h-[3cqw] w-[3cqw]" />
            <span className="min-w-0 flex-1 truncate font-semibold">{tr.title} · {tr.artist}</span>
            <span className="tabular-nums font-bold">{tr.bpm?.toFixed(1) ?? "—"}</span>
            {tr.key && <span className="tabular-nums opacity-70">{tr.key}</span>}
          </div>
          <ScrollWave track={tr} seconds={6} head={0.5} className="h-[7cqw] rounded-[0.4cqw] bg-white/5" />
        </div>
      ))}
      {synced && <p className="text-right text-[1.5cqw] font-bold tracking-[0.2em]">SYNC</p>}
    </div>
  );
}

/** Pick a track (or a same-tempo pair) by seed, so each beat shows a different one. */
export function pickTrack(tracks: FilmAudioTrack[], seed: number) {
  return tracks.length ? tracks[Math.abs(seed) % tracks.length] : null;
}
export function pickPair(tracks: FilmAudioTrack[], seed: number): [FilmAudioTrack, FilmAudioTrack] | null {
  const pairs: [FilmAudioTrack, FilmAudioTrack][] = [];
  tracks.forEach((x, i) => tracks.slice(i + 1).forEach(y => { if (x.bpm && y.bpm && Math.abs(x.bpm - y.bpm) <= 2) pairs.push([x, y]); }));
  return pairs.length ? pairs[Math.abs(seed) % pairs.length] : null;
}
